import React from "react";
import { Box, Typography, Chip, Skeleton } from "@mui/material";

interface LowStockItem {
  ItemId: string;
  ItemName: string;
  Quantity: number;
  ReorderLevel: number;
  Status: string;
}

interface LowStockTableProps {
  data: LowStockItem[];
  loading?: boolean;
}

const LowStockTable: React.FC<LowStockTableProps> = ({ data, loading = false }) => {
  // Number of skeleton rows to show while loading
  const skeletonRows = 5;

  return (
    <Box
      sx={{
        border: "1px solid #e9ecef",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
        backgroundColor: "#ffffff",
      }}
    >
      {}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          p: 3,
          fontWeight: 600,
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e9ecef",
        }}
      >
        {["Item Name", "Quantity", "Reorder Level", "Status"].map((title) => (
          <Typography
            key={title}
            sx={{
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        ))}
      </Box>

      {}
      {(loading ? Array(skeletonRows).fill({}) : data).map((row, i) => (
        <Box
          key={loading ? i : row.ItemId}
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr",
            py: 2.5,
            px: 3,
            alignItems: "center",
            borderBottom:
              i !== (loading ? skeletonRows : data.length) - 1
                ? "1px solid #f1f3f5"
                : "none",
            transition: "all 0.2s ease-in-out",
            "&:hover": !loading
              ? { backgroundColor: "#fafcff", transform: "translateX(4px)" }
              : {},
          }}
        >
          {}
          <Typography
            sx={{
              fontWeight: 500,
              color: "#0f172a",
              fontSize: "0.9375rem",
            }}
          >
            {loading ? <Skeleton width="80%" /> : row.ItemName}
          </Typography>

          {}
          <Typography
            sx={{
              fontWeight: 600,
              color: !loading
                ? row.Quantity <= row.ReorderLevel
                  ? "#dc2626"
                  : "#16a34a"
                : "transparent",
              fontSize: "0.9375rem",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {loading ? <Skeleton width={40} /> : row.Quantity}
          </Typography>

          {}
          <Typography
            sx={{
              color: "#475569",
              fontSize: "0.9375rem",
            }}
          >
            {loading ? <Skeleton width={40} /> : row.ReorderLevel}
          </Typography>

          {}
          {loading ? (
            <Skeleton width={80} height={28} variant="rounded" />
          ) : (
            <Chip
              label={row.Status}
              sx={{
                bgcolor:
                  row.Status === "LOW STOCK"
                    ? "rgba(220, 38, 38, 0.1)"
                    : "rgba(34, 197, 94, 0.1)",
                color: row.Status === "LOW STOCK" ? "#dc2626" : "#16a34a",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "28px",
                borderRadius: "14px",
                width: "fit-content",
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
          )}
        </Box>
      ))}

      {}
      {!loading && data.length === 0 && (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            color: "#94a3b8",
          }}
        >
          <Typography>No low stock items found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default LowStockTable;